import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { useStrapi } from '../hooks/useStrapi';
import { getContactPageSettings } from '../services/strapi';

// Fallback data when Strapi is not available
const FALLBACK = {
  pageTitle: 'Kontakt os',
  heroTitle: 'Kontakt os',
  heroDescription: 'Har du spørgsmål om vores services eller ønsker du en uforpligtende snak? Udfyld formularen nedenfor, så vender vi tilbage hurtigst muligt.',
  formTitle: 'Send os en besked',
  sidebarTitle: 'Kontaktoplysninger',
  email: 'info@sikker-smv.dk',
  phone: '',
  address: '',
  companyName: 'Sikker SMV',
  cvr: 'XXXXXXXX',
  openingHours: '',
  responseTime: 'Vi svarer typisk inden for 1-2 hverdage',
  mapUrl: '',
  whyContactTitle: 'Hvorfor kontakte os?',
  whyContactReasons: [
    'Gratis og uforpligtende rådgivning',
    'Svar inden for 1-2 hverdage',
    'Tilpassede løsninger til din virksomhed',
    'Eksperter inden for GDPR, NIS2 og ISO 27001'
  ],
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const packageParam = useMemo(() => searchParams.get('pakke') || '', [searchParams]);

  const { data: contactData } = useStrapi(getContactPageSettings);

  // Strapi singleType returns { data: { attributes: { ... } } }
  // useStrapi unwraps .data, so contactData = { attributes: { ... } } or the attributes directly
  const settings = contactData?.attributes || contactData || FALLBACK;
  const reasons = settings.whyContactReasons || FALLBACK.whyContactReasons;

  return (
    <>
      {/* Hero */}
      <section className="hero" style={{ padding: '3rem 0' }}>
        <div className="container">
          <h1 className="hero__title" style={{ fontSize: '2.2rem' }}>
            {settings.heroTitle || FALLBACK.heroTitle}
          </h1>
          <p className="hero__subtitle">
            {settings.heroDescription || FALLBACK.heroDescription}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" id="kontakt">
        <div className="container">
          <div className="contact-layout">
            {/* Form */}
            <div className="contact-layout__form">
              <h2 className="section__title" style={{ textAlign: 'left', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                {settings.formTitle || FALLBACK.formTitle}
              </h2>
              <ContactForm preselectedPackage={packageParam} />
            </div>

            {/* Sidebar with contact info */}
            <div className="contact-layout__info">
              <div className="contact-info-card">
                <h3 className="contact-info-card__title">
                  {settings.sidebarTitle || FALLBACK.sidebarTitle}
                </h3>
                
                {(settings.email || FALLBACK.email) && (
                  <div className="contact-info-card__item">
                    <span className="contact-info-card__icon">📧</span>
                    <div>
                      <strong>E-mail</strong>
                      <p><a href={`mailto:${settings.email || FALLBACK.email}`}>{settings.email || FALLBACK.email}</a></p>
                    </div>
                  </div>
                )}

                {settings.phone && (
                  <div className="contact-info-card__item">
                    <span className="contact-info-card__icon">📞</span>
                    <div>
                      <strong>Telefon</strong>
                      <p><a href={`tel:${settings.phone}`}>{settings.phone}</a></p>
                    </div>
                  </div>
                )}

                <div className="contact-info-card__item">
                  <span className="contact-info-card__icon">🕐</span>
                  <div>
                    <strong>Svartid</strong>
                    <p>{settings.responseTime || FALLBACK.responseTime}</p>
                  </div>
                </div>

                {settings.openingHours && (
                  <div className="contact-info-card__item">
                    <span className="contact-info-card__icon">🕘</span>
                    <div>
                      <strong>Åbningstider</strong>
                      <p>{settings.openingHours}</p>
                    </div>
                  </div>
                )}

                <div className="contact-info-card__item">
                  <span className="contact-info-card__icon">🏢</span>
                  <div>
                    <strong>Virksomhed</strong>
                    <p>{settings.companyName || FALLBACK.companyName}</p>
                    {(settings.cvr || FALLBACK.cvr) && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>
                        CVR: {settings.cvr || FALLBACK.cvr}
                      </p>
                    )}
                  </div>
                </div>

                {settings.address && (
                  <div className="contact-info-card__item">
                    <span className="contact-info-card__icon">📍</span>
                    <div>
                      <strong>Adresse</strong>
                      <p>{settings.address}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="contact-info-card" style={{ marginTop: '1.5rem' }}>
                <h3 className="contact-info-card__title">
                  {settings.whyContactTitle || FALLBACK.whyContactTitle}
                </h3>
                <ul className="contact-info-card__list">
                  {reasons.map((reason, i) => (
                    <li key={i}>✓ {reason}</li>
                  ))}
                </ul>
              </div>

              {settings.mapUrl && (
                <div className="contact-info-card" style={{ marginTop: '1.5rem' }}>
                  <h3 className="contact-info-card__title">Find os</h3>
                  <div style={{ width: '100%', height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      title="Kort"
                      src={settings.mapUrl}
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
